// Loan detail: the borrowing header plus its full repayment timetable
// (the amortization schedule) — principal vs interest per month, what's
// paid, and what's still owed. Recording the next payment opens a shared
// confirmation dialog; the post itself is unchanged.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { getLoan, postLoanPayment, type LoanDetail } from "@/lib/api";
import { useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "active":
      return "info";
    case "paid_off":
    case "closed":
      return "success";
    case "defaulted":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Basis points (625 = 6.25%) rendered with both readable forms. */
function rateLabel(bp: number): string {
  return `${(bp / 100).toFixed(2)}% p.a. (${bp} bp)`;
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

export function LoanDetailPage() {
  const { loanId } = useParams({ strict: false }) as {
    loanId: string;
  };
  const [detail, setDetail] = useState<LoanDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const accountLabels = useAccountLabels();

  const reload = useCallback(() => {
    if (!loanId) return;
    setError(null);
    getLoan(loanId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [loanId]);

  useEffect(reload, [reload]);

  const nextEntry = detail?.schedule.find((entry) => !entry.posted);

  const recordPayment = async () => {
    if (!loanId || !nextEntry || posting) return;
    setPosting(true);
    setError(null);
    try {
      await postLoanPayment(loanId, { date: nextEntry.date.slice(0, 10) });
      setConfirming(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirming(false);
    } finally {
      setPosting(false);
    }
  };

  const paid = detail
    ? detail.schedule
        .filter((entry) => entry.posted)
        .reduce((sum, entry) => sum + entry.total_payment_cents, 0)
    : 0;
  const remaining = detail
    ? detail.loan.principal_cents - detail.schedule
        .filter((entry) => entry.posted)
        .reduce((sum, entry) => sum + entry.principal_cents, 0)
    : 0;

  const accountLabel = (accountId: string) =>
    accountLabels.get(accountId) ?? (
      <span title={accountId}>Unknown account</span>
    );

  const entryStatus = (entry: LoanDetail["schedule"][number]) =>
    entry.posted ? (
      entry.journal_entry_id ? (
        <Link
          to="/journal/$entryId"
          params={{ entryId: entry.journal_entry_id }}
          className="underline-offset-4 hover:underline"
          title={entry.journal_entry_id}
        >
          <StatusDot tone="success" label="Paid" />
        </Link>
      ) : (
        <StatusDot tone="success" label="Paid" />
      )
    ) : (
      <StatusDot tone="idle" label="Upcoming" />
    );

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/loans"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Loans
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.loan.name ?? loanId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.loan.name}
            description={
              <>
                {money(detail.loan.principal_cents)} at{" "}
                {rateLabel(detail.loan.interest_rate_bp)} over{" "}
                {detail.loan.term_months}{" "}
                {detail.loan.term_months === 1 ? "month" : "months"} ·{" "}
                <StatusDot
                  tone={statusTone(detail.loan.status)}
                  label={plainStatus(detail.loan.status)}
                />
              </>
            }
            actions={
              nextEntry ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setConfirming(true)}
                  disabled={posting}
                >
                  {posting ? "Recording…" : "Record payment"}
                </Button>
              ) : null
            }
          />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Monthly slice
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(detail.loan.monthly_payment_cents)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Paid so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(paid)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Still owed
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {money(remaining)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Accounts
              </p>
              <p
                className="mt-1 text-sm"
                title="Principal (liability) account / interest (expense) account"
              >
                {accountLabel(detail.loan.principal_account_id)} /{" "}
                {accountLabel(detail.loan.interest_account_id)}
              </p>
            </div>
          </div>

          <section aria-label="Repayment timetable" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              Repayment timetable
            </h2>
            <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">Slice</TableHead>
                      <TableHead className="text-right">Still owed</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.schedule.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          This loan has no repayment timetable yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.schedule.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="tabular-nums">
                            {entry.period_number}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(entry.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entry.principal_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entry.interest_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entry.total_payment_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(entry.remaining_balance_cents)}
                          </TableCell>
                          <TableCell>{entryStatus(entry)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="loan-schedule-stack"
            >
              {detail.schedule.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  This loan has no repayment timetable yet.
                </p>
              ) : (
                detail.schedule.map((entry) => (
                  <article
                    key={entry.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <span className="record-title">
                        Payment {entry.period_number}
                      </span>
                      <span className="tabular-nums">
                        {money(entry.total_payment_cents)}
                      </span>
                    </div>
                    <CardField label="Due">
                      {new Date(entry.date).toLocaleDateString()}
                    </CardField>
                    <CardField label="Principal">
                      {money(entry.principal_cents)}
                    </CardField>
                    <CardField label="Interest">
                      {money(entry.interest_cents)}
                    </CardField>
                    <CardField label="Still owed">
                      {money(entry.remaining_balance_cents)}
                    </CardField>
                    <CardField label="Status">{entryStatus(entry)}</CardField>
                  </article>
                ))
              )}
            </div>
          </section>
        </>
      ) : null}

      <Dialog
        open={confirming && nextEntry !== undefined}
        onOpenChange={(open) => {
          if (!open && !posting) setConfirming(false);
        }}
        title="Record this payment?"
        description={
          nextEntry
            ? `Posts ${money(nextEntry.total_payment_cents)} due ${new Date(
                nextEntry.date,
              ).toLocaleDateString()} to the ledger as ${money(
                nextEntry.principal_cents,
              )} principal and ${money(
                nextEntry.interest_cents,
              )} interest. This does not send money to a lender.`
            : "There is no unpaid installment left on this loan."
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(false)}
            disabled={posting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void recordPayment()}
            disabled={posting || !nextEntry}
          >
            {posting ? "Recording…" : "Record payment in books"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
